import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft,
  Globe,
  Facebook,
  Instagram,
  TrendingUp,
  Users,
  DollarSign,
  Package,
  Clock,
  Shield,
  Zap,
  Star,
  CheckCircle,
  Briefcase,
  Target,
  Repeat,
  Gift,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import emailjs from '@emailjs/browser';
import PartnerCalculator from '../components/PartnerCalculator';

const PartnerPage = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState('en');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    experience: '',
    message: ''
  });

  const content = language === 'en' ? {
    title: "Start Your Pre-Order Business",
    subtitle: "Partner with Athena's and Build Your Own Shopping Empire in Belize",
    hero: {
      cta: "Become a Partner",
      stats1: "Zero Upfront Investment",
      stats2: "Unlimited Earning Potential",
      stats3: "Full Support Provided"
    },
    howItWorks: {
      title: "How It Works",
      step1: "Take Pre-Orders",
      step1desc: "Collect orders from your customers with deposits",
      step2: "Order Through Us",
      step2desc: "Place bulk orders with us at discounted partner rates",
      step3: "We Handle Logistics",
      step3desc: "We purchase, ship, and deliver all packages to you in one batch",
      step4: "Deliver & Profit",
      step4desc: "Distribute to your customers and earn your profit margin"
    },
    benefits: {
      title: "Why Partner With Athena's?",
      benefit1: "No Inventory Risk",
      benefit1desc: "Only order what's already sold. No stock to hold.",
      benefit2: "Partner Discounts",
      benefit2desc: "Special wholesale rates on all orders",
      benefit3: "Bulk Shipping",
      benefit3desc: "Combine multiple customer orders = lower costs per item",
      benefit4: "Marketing Support",
      benefit4desc: "We provide catalogs, product images, and promotional materials",
      benefit5: "Express Option",
      benefit5desc: "Offer 7-10 day SHEIN express to attract more customers",
      benefit6: "Flexible Terms",
      benefit6desc: "No contracts, no minimum orders, grow at your pace"
    },
    earnings: {
      title: "Your Earning Potential",
      subtitle: "Real Examples of What You Can Make",
      example1: {
        orders: "10 orders/week",
        margin: "$10-$20 service fee per order",
        weekly: "$100-$200/week",
        monthly: "$400-$800/month"
      },
      example2: {
        orders: "25 orders/week",
        margin: "$10-$20 service fee per order",
        weekly: "$250-$500/week",
        monthly: "$1,000-$2,000/month"
      },
      example3: {
        orders: "50 orders/week",
        margin: "$10-$20 service fee per order",
        weekly: "$500-$1,000/week",
        monthly: "$2,000-$4,000/month"
      },
      howTitle: "How You Make This Money",
      fee: "Service Fee: The earnings above come entirely from a simple service fee of $10-$20 that YOU charge on each order — you decide the amount.",
      shipping: "Shipping Profit: When we combine everyone's orders into one bulk shipment, you get a big discount on the combined shipping. Charge each customer for their own shipping and keep the savings as extra profit."
    },
    bonuses: {
      title: "Volume Bonuses & Incentives",
      subtitle: "Order more, earn more!",
      tier1: "5-10 orders/month: 5% extra discount",
      tier2: "11-25 orders/month: 10% extra discount",
      tier3: "26-50 orders/month: 15% extra discount",
      tier4: "51+ orders/month: 20% extra discount + Priority shipping",
      loyalty: "Loyalty Program: Earn points on every order for future discounts",
      referral: "Referral Bonus: Bring other partners and earn $50 per referral"
    },
    support: {
      title: "What We Provide",
      item1: "Product Catalog",
      item1desc: "Updated weekly with trending items and prices",
      item2: "Marketing Materials",
      item2desc: "Social media templates, flyers, and promotional content",
      item3: "Training & Guidance",
      item3desc: "How to take orders, price products, and manage customers",
      item4: "Dedicated Support",
      item4desc: "WhatsApp group for partners with priority responses",
      item5: "Order Tracking",
      item5desc: "Real-time updates on all your customer orders",
      item6: "Payment Flexibility",
      item6desc: "Multiple payment options for your convenience"
    },
    ideal: {
      title: "Perfect For",
      type1: "Stay-at-home parents",
      type2: "Students earning extra income",
      type3: "Small business owners expanding",
      type4: "Social media influencers",
      type5: "Anyone wanting flexible income"
    },
    form: {
      title: "Apply to Become a Partner",
      subtitle: "Fill out the form below and we'll contact you within 24 hours",
      name: "Full Name",
      email: "Email Address",
      phone: "Phone Number",
      location: "Your Location in Belize",
      experience: "Previous Sales/Business Experience (if any)",
      message: "Why do you want to become a partner?",
      submit: "Submit Application",
      success: "Application submitted! We'll contact you within 24 hours."
    }
  } : {
    title: "Inicie Su Negocio de Pre-Órdenes",
    subtitle: "Asóciese con Athena's y Construya Su Propio Imperio de Compras en Belice",
    hero: {
      cta: "Conviértase en Socio",
      stats1: "Cero Inversión Inicial",
      stats2: "Potencial de Ganancias Ilimitado",
      stats3: "Apoyo Completo"
    },
    howItWorks: {
      title: "Cómo Funciona",
      step1: "Tome Pre-Órdenes",
      step1desc: "Recopile pedidos de sus clientes con depósitos",
      step2: "Ordene a Través de Nosotros",
      step2desc: "Haga pedidos al por mayor con nosotros a tarifas de socio con descuento",
      step3: "Manejamos la Logística",
      step3desc: "Compramos, enviamos y entregamos todos los paquetes en un lote",
      step4: "Entregue y Gane",
      step4desc: "Distribuya a sus clientes y gane su margen de beneficio"
    },
    benefits: {
      title: "¿Por Qué Asociarse con Athena's?",
      benefit1: "Sin Riesgo de Inventario",
      benefit1desc: "Solo ordene lo que ya está vendido. Sin stock que mantener.",
      benefit2: "Descuentos de Socio",
      benefit2desc: "Tarifas mayoristas especiales en todos los pedidos",
      benefit3: "Envío al Por Mayor",
      benefit3desc: "Combine múltiples pedidos de clientes = costos más bajos por artículo",
      benefit4: "Apoyo de Marketing",
      benefit4desc: "Proporcionamos catálogos, imágenes de productos y materiales promocionales",
      benefit5: "Opción Exprés",
      benefit5desc: "Ofrezca entrega exprés SHEIN de 7-10 días para atraer más clientes",
      benefit6: "Términos Flexibles",
      benefit6desc: "Sin contratos, sin pedidos mínimos, crezca a su ritmo"
    },
    earnings: {
      title: "Su Potencial de Ganancias",
      subtitle: "Ejemplos Reales de lo Que Puede Ganar",
      example1: {
        orders: "10 pedidos/semana",
        margin: "$10-$20 tarifa de servicio por pedido",
        weekly: "$100-$200/semana",
        monthly: "$400-$800/mes"
      },
      example2: {
        orders: "25 pedidos/semana",
        margin: "$10-$20 tarifa de servicio por pedido",
        weekly: "$250-$500/semana",
        monthly: "$1,000-$2,000/mes"
      },
      example3: {
        orders: "50 pedidos/semana",
        margin: "$10-$20 tarifa de servicio por pedido",
        weekly: "$500-$1,000/semana",
        monthly: "$2,000-$4,000/mes"
      },
      howTitle: "Cómo Ganas Este Dinero",
      fee: "Tarifa de Servicio: Las ganancias anteriores provienen completamente de una simple tarifa de servicio de $10-$20 que USTED cobra en cada pedido — usted decide el monto.",
      shipping: "Ganancia por Envío: Cuando combinamos todos los pedidos en un solo envío al por mayor, usted obtiene un gran descuento en el envío combinado. Cobre a cada cliente su propio envío y quédese con el ahorro como ganancia adicional."
    },
    bonuses: {
      title: "Bonificaciones e Incentivos por Volumen",
      subtitle: "¡Ordene más, gane más!",
      tier1: "5-10 pedidos/mes: 5% de descuento adicional",
      tier2: "11-25 pedidos/mes: 10% de descuento adicional",
      tier3: "26-50 pedidos/mes: 15% de descuento adicional",
      tier4: "51+ pedidos/mes: 20% de descuento adicional + Envío prioritario",
      loyalty: "Programa de Lealtad: Gane puntos en cada pedido para futuros descuentos",
      referral: "Bono por Referencia: Traiga otros socios y gane $50 por referencia"
    },
    support: {
      title: "Lo Que Proporcionamos",
      item1: "Catálogo de Productos",
      item1desc: "Actualizado semanalmente con artículos de tendencia y precios",
      item2: "Materiales de Marketing",
      item2desc: "Plantillas de redes sociales, volantes y contenido promocional",
      item3: "Capacitación y Orientación",
      item3desc: "Cómo tomar pedidos, fijar precios y gestionar clientes",
      item4: "Soporte Dedicado",
      item4desc: "Grupo de WhatsApp para socios con respuestas prioritarias",
      item5: "Seguimiento de Pedidos",
      item5desc: "Actualizaciones en tiempo real de todos los pedidos de sus clientes",
      item6: "Flexibilidad de Pago",
      item6desc: "Múltiples opciones de pago para su conveniencia"
    },
    ideal: {
      title: "Perfecto Para",
      type1: "Padres que trabajan desde casa",
      type2: "Estudiantes que ganan ingresos extra",
      type3: "Propietarios de pequeñas empresas en expansión",
      type4: "Influencers de redes sociales",
      type5: "Cualquiera que quiera ingresos flexibles"
    },
    form: {
      title: "Solicite Ser Socio",
      subtitle: "Complete el formulario a continuación y nos comunicaremos con usted en 24 horas",
      name: "Nombre Completo",
      email: "Correo Electrónico",
      phone: "Número de Teléfono",
      location: "Su Ubicación en Belice",
      experience: "Experiencia Previa en Ventas/Negocios (si tiene)",
      message: "¿Por qué quiere convertirse en socio?",
      submit: "Enviar Solicitud",
      success: "¡Solicitud enviada! Nos comunicaremos con usted en 24 horas."
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.location) {
      toast.error(language === 'en' ? 'Please fill in all required fields' : 'Complete todos los campos requeridos');
      return;
    }

    setIsSubmitting(true);

    try {
      // Template params for partner application
      const templateParams = {
        applicant_name: formData.name,
        applicant_email: formData.email,
        applicant_phone: formData.phone,
        applicant_location: formData.location,
        applicant_experience: formData.experience || 'None provided',
        applicant_message: formData.message || 'No additional message'
      };

      // Send email using EmailJS with dedicated partner template
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_PARTNER_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      toast.success(content.form.success);
      
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          location: '',
          experience: '',
          message: ''
        });
      }, 2000);

    } catch (error) {
      console.error('EmailJS Error:', error);
      toast.error(
        language === 'en' 
          ? 'Failed to submit application. Please try again or contact us via WhatsApp.' 
          : 'Error al enviar la solicitud. Por favor intente de nuevo o contáctenos por WhatsApp.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('partner-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f7f5f2] to-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/90 backdrop-blur-md shadow-sm z-50">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-[#8b1538] hover:text-[#6b0f2a]"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">
                {language === 'en' ? 'Back to Home' : 'Volver'}
              </span>
            </Button>
            
            <img 
              src="https://customer-assets.emergentagent.com/job_57e2dd96-4e50-451b-8abf-042890e7a51b/artifacts/m648krxt_Athena%27s%20Logo.png"
              alt="Athena's"
              className="h-12 w-auto object-contain cursor-pointer"
              onClick={() => navigate('/')}
            />
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLanguage(language === 'en' ? 'es' : 'en')}
              className="flex items-center gap-2"
            >
              <Globe className="w-4 h-4" />
              {language === 'en' ? 'ES' : 'EN'}
            </Button>
            <a 
              href="https://www.facebook.com/profile.php?id=61584846392424" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#8b1538] hover:text-[#6b0f2a] transition-colors"
            >
              <Facebook className="w-6 h-6" />
            </a>
            <a 
              href="https://www.instagram.com/athenasonlineshoppingservices/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#8b1538] hover:text-[#6b0f2a] transition-colors"
            >
              <Instagram className="w-6 h-6" />
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-[#8b1538] via-[#a01e47] to-[#8b1538]">
        <div className="container mx-auto text-center">
          <div className="inline-block mb-4 px-6 py-2 bg-[#fad24b] rounded-full">
            <span className="text-[#1e1919] font-bold flex items-center gap-2 justify-center">
              <Briefcase className="w-5 h-5" />
              {language === 'en' ? 'Business Opportunity' : 'Oportunidad de Negocio'}
            </span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            {content.title}
          </h1>
          <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
            {content.subtitle}
          </p>
          
          <Button 
            size="lg"
            onClick={scrollToForm}
            className="bg-[#fad24b] hover:bg-[#f5c935] text-[#1e1919] px-10 py-6 text-lg font-bold transition-all transform hover:scale-105"
          >
            <TrendingUp className="w-5 h-5 mr-2" />
            {content.hero.cta}
          </Button>

          <div className="grid md:grid-cols-3 gap-6 mt-12 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-lg">
              <DollarSign className="w-10 h-10 text-[#fad24b] mx-auto mb-2" />
              <p className="text-white font-bold">{content.hero.stats1}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-lg">
              <TrendingUp className="w-10 h-10 text-[#fad24b] mx-auto mb-2" />
              <p className="text-white font-bold">{content.hero.stats2}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-lg">
              <Shield className="w-10 h-10 text-[#fad24b] mx-auto mb-2" />
              <p className="text-white font-bold">{content.hero.stats3}</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-[#1e1919] mb-16">
            {content.howItWorks.title}
          </h2>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: Users, title: content.howItWorks.step1, desc: content.howItWorks.step1desc, color: '#fad24b' },
              { icon: Package, title: content.howItWorks.step2, desc: content.howItWorks.step2desc, color: '#3dd3ee' },
              { icon: Zap, title: content.howItWorks.step3, desc: content.howItWorks.step3desc, color: '#ff8c19' },
              { icon: DollarSign, title: content.howItWorks.step4, desc: content.howItWorks.step4desc, color: '#8b1538' }
            ].map((step, index) => {
              const Icon = step.icon;
              return (
                <Card key={index} className="border-2 hover:border-[#8b1538]/30 transition-all">
                  <CardContent className="p-6 text-center">
                    <div 
                      className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                      style={{ backgroundColor: `${step.color}20` }}
                    >
                      <Icon className="w-8 h-8" style={{ color: step.color }} />
                    </div>
                    <div className="w-10 h-10 rounded-full bg-[#8b1538] text-white flex items-center justify-center mx-auto mb-3 font-bold">
                      {index + 1}
                    </div>
                    <h3 className="text-xl font-bold text-[#1e1919] mb-3">{step.title}</h3>
                    <p className="text-[#736c64]">{step.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 bg-[#f7f5f2]">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-[#1e1919] mb-16">
            {content.benefits.title}
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: content.benefits.benefit1, desc: content.benefits.benefit1desc, icon: Shield },
              { title: content.benefits.benefit2, desc: content.benefits.benefit2desc, icon: Star },
              { title: content.benefits.benefit3, desc: content.benefits.benefit3desc, icon: Package },
              { title: content.benefits.benefit4, desc: content.benefits.benefit4desc, icon: Target },
              { title: content.benefits.benefit5, desc: content.benefits.benefit5desc, icon: Zap },
              { title: content.benefits.benefit6, desc: content.benefits.benefit6desc, icon: CheckCircle }
            ].map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="border-2 border-[#8b1538]/20 hover:border-[#8b1538] transition-all">
                  <CardContent className="p-6">
                    <Icon className="w-10 h-10 text-[#8b1538] mb-4" />
                    <h3 className="text-lg font-bold text-[#1e1919] mb-2">{benefit.title}</h3>
                    <p className="text-sm text-[#736c64]">{benefit.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Earnings Potential */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-[#1e1919] mb-4">
            {content.earnings.title}
          </h2>
          <p className="text-center text-[#736c64] text-lg mb-12">
            {content.earnings.subtitle}
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[content.earnings.example1, content.earnings.example2, content.earnings.example3].map((example, index) => (
              <Card 
                key={index} 
                className={`border-4 ${index === 1 ? 'border-[#8b1538] transform scale-105' : 'border-[#fad24b]/30'} relative`}
              >
                {index === 1 && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#8b1538] text-white px-4 py-1 rounded-full text-sm font-bold">
                    {language === 'en' ? 'Most Popular' : 'Más Popular'}
                  </div>
                )}
                <CardContent className="p-8 text-center">
                  <h3 className="text-2xl font-bold text-[#1e1919] mb-4">{example.orders}</h3>
                  <div className="space-y-2 mb-6">
                    <p className="text-[#736c64]">{example.margin}</p>
                    <div className="border-t-2 border-[#fad24b] my-4"></div>
                    <p className="text-3xl font-bold text-[#8b1538]">{example.weekly}</p>
                    <p className="text-xl text-[#736c64]">{example.monthly}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* How You Earn */}
          <div className="mt-16 max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold text-center text-[#1e1919] mb-8">
              {content.earnings.howTitle}
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-[#8b1538]/5 border-2 border-[#8b1538]/20 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-6 h-6 text-[#8b1538]" />
                  <h4 className="text-lg font-bold text-[#1e1919]">
                    {language === 'en' ? 'Service Fee' : 'Tarifa de Servicio'}
                  </h4>
                </div>
                <p className="text-[#736c64]">{content.earnings.fee}</p>
              </div>
              <div className="bg-[#3dd3ee]/10 border-2 border-[#3dd3ee]/30 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Package className="w-6 h-6 text-[#3dd3ee]" />
                  <h4 className="text-lg font-bold text-[#1e1919]">
                    {language === 'en' ? 'Shipping Profit' : 'Ganancia por Envío'}
                  </h4>
                </div>
                <p className="text-[#736c64]">{content.earnings.shipping}</p>
              </div>
            </div>
          </div>

          {/* Earnings Calculator */}
          <div className="mt-12">
            <PartnerCalculator language={language} />
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#fad24b]/20 to-[#ff8c19]/20">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <Gift className="w-16 h-16 text-[#8b1538] mx-auto mb-4" />
            <h2 className="text-4xl md:text-5xl font-bold text-[#1e1919] mb-4">
              {content.bonuses.title}
            </h2>
            <p className="text-xl text-[#736c64]">{content.bonuses.subtitle}</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Card className="border-2 border-[#8b1538]/30">
              <CardContent className="p-6">
                <h3 className="font-bold text-[#1e1919] mb-4 text-lg">{language === 'en' ? 'Volume Discounts' : 'Descuentos por Volumen'}</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[#8b1538] flex-shrink-0 mt-0.5" />
                    <span className="text-[#736c64]">{content.bonuses.tier1}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[#8b1538] flex-shrink-0 mt-0.5" />
                    <span className="text-[#736c64]">{content.bonuses.tier2}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[#8b1538] flex-shrink-0 mt-0.5" />
                    <span className="text-[#736c64]">{content.bonuses.tier3}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[#8b1538] flex-shrink-0 mt-0.5" />
                    <span className="text-[#736c64] font-bold">{content.bonuses.tier4}</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#3dd3ee]/30">
              <CardContent className="p-6">
                <h3 className="font-bold text-[#1e1919] mb-4 text-lg">{language === 'en' ? 'Extra Benefits' : 'Beneficios Adicionales'}</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <Repeat className="w-5 h-5 text-[#3dd3ee] flex-shrink-0 mt-0.5" />
                    <span className="text-[#736c64]">{content.bonuses.loyalty}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Users className="w-5 h-5 text-[#3dd3ee] flex-shrink-0 mt-0.5" />
                    <span className="text-[#736c64]">{content.bonuses.referral}</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Support */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-[#1e1919] mb-16">
            {content.support.title}
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: content.support.item1, desc: content.support.item1desc },
              { title: content.support.item2, desc: content.support.item2desc },
              { title: content.support.item3, desc: content.support.item3desc },
              { title: content.support.item4, desc: content.support.item4desc },
              { title: content.support.item5, desc: content.support.item5desc },
              { title: content.support.item6, desc: content.support.item6desc }
            ].map((item, index) => (
              <Card key={index} className="border-2 border-[#fad24b]/30 hover:border-[#fad24b] transition-all">
                <CardContent className="p-6">
                  <CheckCircle className="w-8 h-8 text-[#8b1538] mb-3" />
                  <h3 className="text-lg font-bold text-[#1e1919] mb-2">{item.title}</h3>
                  <p className="text-sm text-[#736c64]">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal For */}
      <section className="py-16 px-4 bg-[#f7f5f2]">
        <div className="container mx-auto max-w-4xl">
          <h3 className="text-3xl font-bold text-center text-[#1e1919] mb-8">
            {content.ideal.title}
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              content.ideal.type1,
              content.ideal.type2,
              content.ideal.type3,
              content.ideal.type4,
              content.ideal.type5
            ].map((type, index) => (
              <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-lg">
                <CheckCircle className="w-6 h-6 text-[#8b1538] flex-shrink-0" />
                <span className="text-[#1e1919] font-semibold">{type}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="partner-form" className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-3xl">
          <Card className="border-2 border-[#8b1538]/20 shadow-xl">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-[#1e1919] mb-2">
                  {content.form.title}
                </h2>
                <p className="text-[#736c64]">{content.form.subtitle}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-[#1e1919] font-semibold">
                      {content.form.name} *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="mt-2 border-[#736c64]/30 focus:border-[#8b1538]"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-[#1e1919] font-semibold">
                      {content.form.email} *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-2 border-[#736c64]/30 focus:border-[#8b1538]"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone" className="text-[#1e1919] font-semibold">
                      {content.form.phone} *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="mt-2 border-[#736c64]/30 focus:border-[#8b1538]"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="location" className="text-[#1e1919] font-semibold">
                      {content.form.location} *
                    </Label>
                    <Input
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      required
                      className="mt-2 border-[#736c64]/30 focus:border-[#8b1538]"
                      placeholder="e.g., Belize City, Orange Walk, San Pedro"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="experience" className="text-[#1e1919] font-semibold">
                    {content.form.experience}
                  </Label>
                  <Input
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    className="mt-2 border-[#736c64]/30 focus:border-[#8b1538]"
                    placeholder={language === 'en' ? 'Optional' : 'Opcional'}
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-[#1e1919] font-semibold">
                    {content.form.message}
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="mt-2 border-[#736c64]/30 focus:border-[#8b1538]"
                  />
                </div>

                <Button 
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-[#8b1538] hover:bg-[#6b0f2a] text-white py-6 text-lg font-semibold transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {language === 'en' ? 'Sending...' : 'Enviando...'}
                    </>
                  ) : (
                    <>
                      <Briefcase className="w-5 h-5 mr-2" />
                      {content.form.submit}
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1e1919] text-white py-8 px-4">
        <div className="container mx-auto text-center">
          <div className="flex items-center justify-center gap-6 mb-4">
            <a
              href="https://www.facebook.com/profile.php?id=61584846392424"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook-link"
              className="text-[#fad24b] hover:text-white transition-colors"
            >
              <Facebook className="w-6 h-6" />
            </a>
            <a
              href="https://www.instagram.com/athenasonlineshoppingservices/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              className="text-[#fad24b] hover:text-white transition-colors"
            >
              <Instagram className="w-6 h-6" />
            </a>
          </div>
          <p className="text-[#bbb5ae] text-sm mb-3">
            Business Hours: 10 AM - 9 PM (7 Days)
          </p>
          <div className="mb-4 text-sm space-y-1">
            <p className="text-[#bbb5ae]">
              Phone: <a href="tel:+5016735717" data-testid="footer-phone-link" className="text-[#fad24b] hover:text-white transition-colors">6735717</a>
            </p>
            <p className="text-[#bbb5ae]">
              WhatsApp: <a href="https://wa.me/5016735717" target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link" className="text-[#fad24b] hover:text-white transition-colors">Chat with us</a>
            </p>
            <p className="text-[#bbb5ae]">
              Email: <a href="mailto:athenasonlineshoppingservices@gmail.com" data-testid="footer-email-link" className="text-[#fad24b] hover:text-white transition-colors break-all">athenasonlineshoppingservices@gmail.com</a>
            </p>
            <p className="text-[#bbb5ae]">
              <a href="/privacy" data-testid="footer-privacy-link" className="text-[#fad24b] hover:text-white transition-colors">Privacy Policy</a>
            </p>
          </div>
          <p className="text-[#bbb5ae] text-sm">
            © {new Date().getFullYear()} Athena's Online Shopping Services. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PartnerPage;
